import React, { useState, useEffect } from "react";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import {
  fetchAllowedMenus,
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
} from "../Services/APIService";
import CreateSubMediaSource from "../Components/CreateSubMediaSource";
import SubMediaSourceView from "../Components/SubMediaSourceView";
import SubMediaSourceEdit from "../Components/UpdateSubMediaSource";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useLocation } from "react-router-dom";
import OverlayLoader from "../Components/OverlayLoader";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
  createdBy: number;
  createdOn: string;
}

interface SubMediaSource {
  id: number;
  name: string;
  mediaSourceID: number;
  mediaSourceName: string;
  isActive: boolean;
  createdBy: number;
  createdOn: Date;
  isAssignTabShown?: boolean;
}

const SubMediaSource: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
  const [subMediaSources, setSubMediaSources] = useState<SubMediaSource[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showCreateCard, setShowCreateCard] = useState(false);

  const location = useLocation();
  // State for edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [subMediaSourceToEdit, setSubMediaSourceToEdit] =
    useState<SubMediaSource | null>(null);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Function to refresh SubMediaSource data
  const refreshSubMediaSources = () => {
    setRefreshTrigger((prev) => prev + 1);
    setShowCreateCard(false);
  };

  // Function to handle create completion
  const handleCreateComplete = (data: SubMediaSource) => {
    setShowCreateCard(false);
    refreshSubMediaSources();
    const dataWithTabFlag = {
      ...data,
      isAssignTabShown: true,
    };
    setSubMediaSourceToEdit(dataWithTabFlag);
    setIsEditing(true);
    setShowCreateCard(false);

    // Scroll to the update card
    setTimeout(() => {
      const updateCard = document.getElementById("updateGroupCard");
      if (updateCard) {
        updateCard.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);

    // Refresh the list
    refreshSubMediaSources();
  };

  // Function to handle create cancel
  const handleCreateCancel = () => {
    setShowCreateCard(false);
  };
  // Function to handle edit click from SubMediaSourceView
  const handleEditClick = (subMediaSource: SubMediaSource) => {
    setSubMediaSourceToEdit({ ...subMediaSource, isAssignTabShown: false });
    setIsEditing(true);
    if (showCreateCard) {
      setShowCreateCard(false);
    }
    // Scroll to the update card
    setTimeout(() => {
      const updateCard = document.getElementById("updateGroupCard");
      if (updateCard) {
        updateCard.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Function to handle completion of edit (success or cancel)
  const handleEditComplete = () => {
    setIsEditing(false);
    setSubMediaSourceToEdit(null);
    refreshSubMediaSources();
  };

  // Function to handle update cancel
  const handleUpdateCancel = () => {
    setIsEditing(false);
    setSubMediaSourceToEdit(null);
  };

  // Function to toggle create form
  const toggleCreateForm = () => {
    setShowCreateCard(!showCreateCard);
    // Hide update form if it's open
    if (isEditing) {
      setIsEditing(false);
      setSubMediaSourceToEdit(null);
    }
  };

  // Fetch menu data
  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, []);

  // Fetch MediaSource and SubMediaSource data, then transform the data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [mediaSourceData, subMediaSourceData] = await Promise.all([
          fetchMeadiaSourceData(),
          fetchAllSubMediaSource(),
        ]);
        setMediaSources(mediaSourceData);

        // Transform subMediaSourceData to include mediaSourceName
        const transformedSubMediaSources = subMediaSourceData.map(
          (subMediaSource) => {
            const mediaSource = mediaSourceData.find(
              (source) => source.sourceId === subMediaSource.mediaSourceID
            );
            return {
              ...subMediaSource,
              mediaSourceName: mediaSource ? mediaSource.sourceName : "Unknown",
            };
          }
        );

        setSubMediaSources(transformedSubMediaSources);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [refreshTrigger]);

  return (
    <div className="wrapper">
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  {showCreateCard && (
                    <CreateSubMediaSource
                      mediaSources={mediaSources}
                      onCreateComplete={handleCreateComplete}
                      onCancel={handleCreateCancel}
                    />
                  )}

                  {isEditing && subMediaSourceToEdit ? (
                    <div id="updateGroupCard">
                      <SubMediaSourceEdit
                        initialSubMediaSource={subMediaSourceToEdit}
                        onEditComplete={handleEditComplete}
                        onCancel={handleUpdateCancel}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="col-12 col-lg-12">
                  <SubMediaSourceView
                    subMediaSources={subMediaSources}
                    onEditClick={handleEditClick}
                    onAddClick={toggleCreateForm}
                    showAddButton={!showCreateCard && !isEditing}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={isLoading}
        message="Loading Submedia Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default SubMediaSource;
